import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, KPI, KPIRow, Loading, Msg, Section, Select } from '../kit';
import { RunTable } from './Runs';
import {
  Dot, HEALTH_LEVEL, MODE_LABEL, Pill, ProbeBadge, type RecrawlCtx, RetryMsg, RunLink, SplitBar, StatusPill, type Tone,
  UniLink, arr, fmtAgo, fmtDur, fmtIST, fmtN, fmtPct, fmtUsd, n0, num, obj, probeTitle, queueText, runTone, scrapeLine, scrapeResults,
  str, useLiveGet, usePoll,
} from './shared';
import { RUN_MODE_INFO, RunAllModal, RunButton, type RunMode, RunStartModal } from './RunStart';
import { IfApprovedCard } from './IfApproved';

/**
 * Recrawl → Overview (GET overview): is anything running or waiting for a
 * person, what needs attention, when the scheduler will start what, and one
 * health tile per university (the latest dry / full run and its results,
 * listed technologies, drift between the master and the live table, request
 * health of the last crawl, and the latest probe as a reachability line).
 *
 * Refreshes every 8 s while a run is going, queued or post-loading, and
 * reloads the whole panel once the last of them finishes (so the tables and
 * the drawers see the new run state).
 *
 * Starting runs by hand: the "Start a run" card (a university and a kind of run, then a confirmation
 * that says what happens — RunStart.tsx), "Queue all universities" (what would queue and what is
 * skipped and why, then one run each, started one at a time) and a Run ▸ action on every tile.
 * "If approved" (IfApproved.tsx): the website and master counts now → after approval, for the runs
 * waiting for approval and for every university's latest results.
 */

// ---------------------------------------------------------------------------
// The scheduler / kill-switch banner (also on the Settings tab)
// ---------------------------------------------------------------------------
export const SchedulerBanner: React.FC<{ scheduler: any; env: any; onSettings?: () => void }> = ({ scheduler, env, onSettings }) => {
  const sc = obj(scheduler);
  const e = obj(env);
  const envAuto = sc.env_auto ?? e.auto;
  const enabled = !!sc.enabled;
  const q = arr(sc.quiet_hours_ist);
  const quiet = q.length === 2 ? `${q[0]}:00–${q[1]}:00 IST` : '';
  const spend = obj(sc.month_spend);
  const ls = obj(sc.last_sweep);
  let tone: Tone; let head: string; let text: string;
  if (envAuto && enabled) {
    tone = 'green'; head = 'Automatic crawling is ON';
    text = `The scheduler starts at most one due university every ${sc.sweep_every_min ?? '?'} min${quiet ? `, never inside the quiet hours (${quiet})` : ''}, and only while no other run is going and the monthly budget lasts.`;
  } else if (enabled && !envAuto) {
    tone = 'amber'; head = 'Scheduler switch on, but blocked on this server';
    text = 'RECRAWL_AUTO is not 1 in the API environment, so nothing starts by itself. Run now, Probe and Dry run still work.';
  } else if (envAuto && !enabled) {
    tone = 'amber'; head = 'Automatic crawling is OFF (switch)';
    text = 'RECRAWL_AUTO=1 is set on the server but the scheduler switch is off: nothing starts by itself. Run now, Probe and Dry run still work.';
  } else {
    tone = 'grey'; head = 'Automatic crawling is OFF';
    text = 'RECRAWL_AUTO is unset and the scheduler switch is off (the safe default after a deploy). Run now, Probe and Dry run still work.';
  }
  const border = tone === 'green' ? 'border-green-300 bg-green-50' : tone === 'amber' ? 'border-amber-300 bg-amber-50' : 'border-grayscale-600/40 bg-white';
  const problems: string[] = [];
  if (e.launcher === 'systemd' && e.venv_ok === false) problems.push(`Worker venv missing (${str(e.venv)}): runs cannot start on this server.`);
  if (e.data_dir_ok === false) problems.push(`Data folder ${str(e.data_dir)} does not exist yet (created on the first run).`);
  if (e.replicate_token === false) problems.push('No Replicate token found: the enrich step will fail for new / modified records.');
  const cap = num(spend.cap_usd);
  return (
    <div className={`rounded-xl border p-3 flex flex-col gap-2 ${border}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          <div className="flex items-center gap-2">
            <Dot tone={tone} size={10} />
            <span className="text-sm font-bold text-grayscale-1300 font-grotesk">{head}</span>
            {sc.quiet_now && <Pill tone="grey" title="Inside the IST quiet hours: no scheduled start now">quiet hours now</Pill>}
          </div>
          <p className="text-[12.5px] text-grayscale-1200 mt-0.5">{text}</p>
        </div>
        <div className="flex flex-col gap-1 text-xs text-grayscale-1200 min-w-[13rem]">
          <span>RECRAWL_AUTO <b className={envAuto ? 'text-green-700' : 'text-grayscale-1300'}>{envAuto ? '= 1' : 'not set'}</b>
            {' '}· switch <b>{enabled ? 'on' : 'off'}</b> · thread <b>{sc.running ? 'running' : 'not started'}</b></span>
          {sc.next_sweep_at && <span>next sweep {fmtIST(sc.next_sweep_at)} ({fmtAgo(sc.next_sweep_at)})</span>}
          {(ls.skipped || ls.started || ls.error) && (
            <span title={str(ls.skipped || ls.error)} className="truncate max-w-[24rem]">
              last sweep {fmtAgo(ls.at)}: {ls.started ? <>started run <b>#{obj(ls.started).id}</b> ({str(obj(ls.started).code)})</>
                : ls.error ? <span className="text-red-700">{str(ls.error)}</span> : str(ls.skipped)}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1 min-w-[12rem]">
          <span className="text-xs text-grayscale-1200">
            Enrichment this month <b>{fmtUsd(spend.spent_usd)}</b>{cap !== null ? <> of {fmtUsd(cap)}</> : null}
          </span>
          {cap ? (
            <SplitBar parts={[{ key: 'spent', value: Math.min(n0(spend.spent_usd), cap), color: n0(spend.spent_usd) >= cap ? '#D0443A' : '#AF8E5E' }]} total={cap} />
          ) : null}
          {onSettings && <button type="button" onClick={onSettings} className="text-xs text-primary-700 underline underline-offset-2 self-start">Scheduler settings →</button>}
        </div>
      </div>
      {problems.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-amber-900">{problems.map(p => <li key={p}>{p}</li>)}</ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------
const ATTN: Record<string, { tone: Tone; label: string }> = {
  failing: { tone: 'red', label: 'failing' },
  blocked: { tone: 'red', label: 'blocked' },
  needs_attention: { tone: 'amber', label: 'needs attention' },
  waiting: { tone: 'amber', label: 'waiting on you' },
  drift: { tone: 'purple', label: 'drift vs live' },
  stale: { tone: 'grey', label: 'stale' },
  never_run: { tone: 'grey', label: 'never crawled' },
};

const countsLine = (r: any) => {
  const c = obj(obj(r).counts);
  const bits: string[] = [];
  if (num(c.new) !== null) bits.push(`+${fmtN(c.new)} new`);
  if (num(c.modified) !== null) bits.push(`~${fmtN(c.modified)} modified`);
  if (num(c.removed_proposed) !== null) bits.push(`−${fmtN(c.removed_proposed)} removals`);
  if (!bits.length && num(c.mapped) !== null) bits.push(`${fmtN(c.mapped)} mapped`);
  if (!bits.length && num(c.scraped) !== null) bits.push(`${fmtN(c.scraped)} scraped so far`);
  return bits.join(' · ');
};

const RunLine: React.FC<{ r: any; ctx: RecrawlCtx; review?: boolean }> = ({ r, ctx, review }) => {
  const v = obj(r);
  const g = obj(v.guard);
  const reasons = [...arr(g.reasons), ...arr(obj(g.approval).reasons)].map(str).filter(Boolean);
  return (
    <div className="flex items-start gap-2 py-1.5 border-t border-grayscale-200 first:border-t-0">
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5 flex-wrap text-sm">
          <RunLink ctx={ctx} id={v.id} />
          <UniLink ctx={ctx} code={v.code} name />
          <StatusPill status={v.status} />
          <span className="text-xs text-grayscale-1100">{MODE_LABEL[str(v.mode)] || str(v.mode)} · {str(v.trigger)}</span>
        </div>
        <div className="text-xs text-grayscale-1200">
          {countsLine(v) || '—'}
          <span className="text-grayscale-1100"> · {fmtAgo(v.finished_at || v.started_at || v.created_at)}</span>
          {v.status === 'running' && v.current_step && <span> · step <b>{str(v.current_step)}</b> · {fmtDur(v.duration_s)}</span>}
          {queueText(v) && <span> · <b>{queueText(v)}</b></span>}
        </div>
        {reasons.length > 0 && (
          <div className="text-[11px] text-amber-800 truncate" title={reasons.join('\n')}>{Array.from(new Set(reasons)).slice(0, 3).join(' · ')}</div>
        )}
      </div>
      <Btn small kind={review ? 'primary' : 'subtle'} onClick={() => ctx.openRun(str(v.id))}>{review ? 'Review' : 'Open'}</Btn>
    </div>
  );
};

const HealthGrid: React.FC<{ ctx: RecrawlCtx; onRun: (code: string) => void }> = ({ ctx, onRun }) => {
  const [sort, setSort] = useState('problems');
  const tiles = useMemo(() => {
    const list = ctx.universities.map(u => {
      const listed = n0(u.master.active) + n0(u.master.missing);
      const drift = num(u.drift);
      const driftBad = drift !== null && listed > 0 && Math.abs(drift) > Math.max(5, 0.02 * listed);
      const h = ctx.health.get(u.code);
      const level = str(h?.recommendation?.level) || 'none';
      const st = str(u.last_scrape?.status);
      const probeBad = u.last_probe?.level === 'blocked' || u.last_probe?.level === 'error';
      const bad = (u.needs_attention ? 4 : 0) + (st === 'failed' || st === 'interrupted' ? 3 : 0) + (level === 'bad' ? 2 : 0)
        + (probeBad ? 2 : 0) + (driftBad ? 1 : 0) + (st === 'held' || st === 'awaiting_approval' ? 1 : 0);
      return { u, listed, drift, driftBad, level, bad, h };
    });
    if (sort === 'problems') list.sort((a, b) => b.bad - a.bad || a.u.code.localeCompare(b.u.code));
    else if (sort === 'size') list.sort((a, b) => b.listed - a.listed);
    else if (sort === 'recent') list.sort((a, b) => str(b.u.last_scrape?.created_at).localeCompare(str(a.u.last_scrape?.created_at)));
    return list;
  }, [ctx.universities, ctx.health, sort]);
  if (!ctx.universities.length) return <div className="text-sm text-grayscale-1100 py-4 text-center">No universities loaded.</div>;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
        <Select value={sort} onChange={setSort} options={['problems', 'folder', 'size', 'recent']}
          labels={{ problems: 'Problems first', folder: 'Folder order', size: 'Most technologies', recent: 'Most recent dry / full run' }} />
        <span className="inline-flex items-center gap-1"><Dot tone="green" /> last dry / full run done</span>
        <span className="inline-flex items-center gap-1"><Dot tone="amber" /> waiting / held</span>
        <span className="inline-flex items-center gap-1"><Dot tone="red" /> failed</span>
        <span className="inline-flex items-center gap-1"><Dot tone="grey" /> never crawled</span>
        <span>· the small ring is request health of the last crawl · the bottom line is the latest probe</span>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11.5rem, 1fr))' }}>
        {tiles.map(({ u, listed, drift, driftBad, level, h }) => {
          const sc = u.last_scrape;
          const st = str(sc?.status);
          const tone: Tone = st ? runTone(st) : 'grey';
          const hl = HEALTH_LEVEL[level] || HEALTH_LEVEL.none;
          const results = scrapeResults(sc, true);
          const probe = u.last_probe;
          const scrapeTitle = sc
            ? `latest ${MODE_LABEL[str(sc.mode)] || str(sc.mode)}: run #${str(sc.id)}, ${st.replace(/_/g, ' ')} (${fmtIST(sc.finished_at || sc.started_at || sc.created_at)})${scrapeResults(sc) ? `\n${scrapeResults(sc)}` : ''}`
            : 'never crawled by the engine';
          return (
            <div key={u.code} role="button" tabIndex={0} onClick={() => ctx.openUniversity(u.code)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ctx.openUniversity(u.code); } }}
              title={`${u.name}\n${scrapeTitle}\n${probe ? probeTitle(probe).replace(/\n/g, ' · ') : 'no probe yet'}\nrequest health: ${hl.label}${h?.recommendation?.text ? ` — ${h.recommendation.text}` : ''}${u.due_reason ? `\nschedule: ${u.due_reason}` : ''}`}
              className={`text-left cursor-pointer rounded-lg border px-2.5 py-2 bg-white hover:border-primary-400 hover:bg-primary-100/30 flex flex-col gap-0.5 ${u.needs_attention ? 'border-amber-400' : 'border-grayscale-600/30'}`}>
              <div className="flex items-center gap-1.5">
                <Dot tone={tone} size={9} />
                <span className="text-[13px] font-bold text-grayscale-1300">{u.code}</span>
                {u.kind === 'manual_file' && <span className="text-[10px] text-grayscale-1100">manual</span>}
                {u.paused && <span className="text-[10px] text-amber-800">paused</span>}
                {u.needs_attention && <span className="text-[10px] text-amber-800">attention</span>}
                <span className="ml-auto inline-block w-2.5 h-2.5 rounded-full border-2 shrink-0" title={`request health: ${hl.label}`}
                  style={{ borderColor: level === 'none' ? '#C4C4C4' : (hl.tone === 'green' ? '#2F9E5B' : hl.tone === 'amber' ? '#D08A1E' : '#D0443A') }} />
                <RunButton u={u} onRun={onRun} />
              </div>
              <div className="text-[11px] text-grayscale-1100 truncate">{u.name}</div>
              <div className="text-[11.5px] text-grayscale-1200">
                <b className="text-grayscale-1300">{listed.toLocaleString()}</b> listed
                {drift !== null && drift !== 0 && (
                  <span className={driftBad ? 'text-red-700 font-medium' : 'text-grayscale-1100'} title="Live rows minus master active + missing">
                    {' '}· drift {drift > 0 ? '+' : ''}{drift.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-grayscale-1200 leading-snug">
                {st ? (
                  <>
                    <span className="text-grayscale-1100">{scrapeLine(sc)}</span>
                    <span className="block">{results || (st === 'running' || st === 'queued' ? 'no results yet' : 'no results')}</span>
                  </>
                ) : <span className="text-grayscale-1100">never crawled by the engine</span>}
              </div>
              {probe && (
                <div className="text-[10px] text-grayscale-1100 flex min-w-0">
                  <ProbeBadge probe={probe} className="max-w-full" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Start a run (one university) / queue all
// ---------------------------------------------------------------------------
const START_MODES: RunMode[] = ['dry_run', 'full', 'probe_only'];

const StartRunCard: React.FC<{ ctx: RecrawlCtx; going: number; onStart: (code: string, mode: RunMode) => void; onAll: () => void }> =
  ({ ctx, going, onStart, onAll }) => {
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<RunMode>('dry_run');
  const codeOpts = ['', ...ctx.universities.map(u => u.code)];
  const codeLabels: Record<string, string> = { '': 'Pick a university…' };
  ctx.universities.forEach(u => {
    const open = num(u.open_run_id) !== null ? ` (run #${u.open_run_id} open)` : '';
    codeLabels[u.code] = `${u.code} — ${u.name}${open}`;
  });
  return (
    <Section title="Start a run" right={<Btn small onClick={onAll}>Queue all universities…</Btn>}>
      <div className="flex items-center gap-2 flex-wrap min-w-0 [&_select]:max-w-full">
        <Select value={code} onChange={setCode} options={codeOpts} labels={codeLabels} />
        <Select value={mode} onChange={v => setMode(v as RunMode)} options={START_MODES}
          labels={{ dry_run: 'Dry run', full: 'Full run', probe_only: 'Probe' }} />
        <Btn kind="primary" disabled={!code} onClick={() => onStart(code, mode)}>Start…</Btn>
        <span className="text-xs text-grayscale-1200 flex-1 min-w-[12rem]">
          <b>{RUN_MODE_INFO[mode].label}:</b> {RUN_MODE_INFO[mode].short.toLowerCase()}. You confirm before anything starts.
        </span>
      </div>
      <div className="text-[11px] text-grayscale-1100">
        One run at a time across all universities{going ? ` — ${going} going or queued now; a new run waits its turn` : ''}.
        Every tile below and every row in Universities has a Run ▸ action too.
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// the tab
// ---------------------------------------------------------------------------
const Overview: React.FC<{ ctx: RecrawlCtx }> = ({ ctx }) => {
  // useLiveGet: a failed poll keeps the last overview, so `live` holds and polling carries on until the API answers again.
  const ov = useLiveGet<any>('overview', {}, [ctx.tick]);
  const d = obj(ov.data);
  const queue = arr(d.queue); const awaiting = arr(d.awaiting); const held = arr(d.held);
  const post = arr(d.post_load); const recent = arr(d.recent); const attention = arr(d.attention);
  const active = d.active_run && typeof d.active_run === 'object' ? d.active_run : null;
  const live = !!active || queue.length > 0 || post.length > 0;
  usePoll(ov.reload, 8000, live);
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !live) ctx.reloadAll();
    wasLive.current = live;
  }, [live]);  // eslint-disable-line react-hooks/exhaustive-deps

  const [attnKind, setAttnKind] = useState('');
  const [showAllDue, setShowAllDue] = useState(false);
  const [startFor, setStartFor] = useState<{ code: string; mode?: RunMode } | null>(null);
  const [runAll, setRunAll] = useState(false);

  if (ov.loading && !ov.data) return <Loading label="Loading the recrawl overview…" />;
  if (ov.error && !ov.data) {
    return <Section title="Recrawl" right={<Btn small onClick={ov.reload}>Retry</Btn>}><Msg kind="error">{ov.error}</Msg></Section>;
  }

  const k = obj(d.kpis);
  const master = obj(k.master);
  const listed = n0(master.active) + n0(master.missing);
  const ev = obj(k.events_30d);
  const changes = n0(ev.added) + n0(ev.modified) + n0(ev.removed) + n0(ev.readded) + n0(ev.moved);
  const runs30 = obj(k.runs_30d);
  const total30 = Object.values(runs30).reduce((a: number, b: any) => a + n0(b), 0);
  const failed30 = n0(runs30.failed) + n0(runs30.interrupted);
  const byKind = obj(k.by_kind);
  const spend = obj(k.spend_month);
  const needs = awaiting.length + held.length;
  const drift = num(k.drift_total);

  const sched = obj(d.scheduler);
  const dues = arr(sched.due);
  const scheduled = dues.filter(x => !/^(schedule off|university disabled)/.test(str(x.reason)));
  const dueList = (showAllDue ? dues : scheduled).slice(0, showAllDue ? 200 : 12);

  const kinds = Array.from(new Set(attention.map(a => str(a.kind))));
  const attnShown = attention.filter(a => !attnKind || str(a.kind) === attnKind);

  return (
    <>
      {ov.stale && <RetryMsg error={ov.error} onRetry={ov.reload} stale polling={live} />}
      <SchedulerBanner scheduler={sched} env={d.env} onSettings={() => ctx.goTab('Settings')} />

      <KPIRow>
        <KPI label="Universities" value={fmtN(k.universities ?? ctx.universities.length)}
          hint={`${n0(byKind.scraper)} crawled · ${n0(byKind.manual_file)} manual file`} onClick={() => ctx.goTab('Universities')} />
        <KPI label="Listed in master" value={fmtN(listed)}
          hint={`live ${fmtN(k.live_total)}${drift !== null ? ` · drift ${drift > 0 ? '+' : ''}${drift.toLocaleString()}` : ''} · ${fmtN(master.removed)} removed`} />
        <KPI label="Changes · 30 days" value={fmtN(changes)}
          hint={`+${fmtN(ev.added)} · ~${fmtN(ev.modified)} · −${fmtN(ev.removed)} · ↺${fmtN(ev.readded)} · ↪${fmtN(ev.moved)}`} onClick={() => ctx.goChanges({})} />
        <KPI label="Runs · 30 days" value={fmtN(total30)}
          hint={`${fmtN(runs30.done)} done · ${failed30} failed${total30 ? ` (${fmtPct(failed30, total30)})` : ''}`} onClick={() => ctx.goRuns({})} />
        <KPI label="Needs you" value={fmtN(needs)} hint={`${awaiting.length} awaiting approval · ${held.length} held`}
          active={needs > 0} onClick={() => ctx.goRuns({ status: 'awaiting_approval,held' })} />
        <KPI label="Enrichment · month" value={fmtUsd(spend.usd)}
          hint={`${fmtN(spend.records)} records${num(spend.cap_usd) !== null ? ` · cap ${fmtUsd(spend.cap_usd)}` : ''}`} />
      </KPIRow>

      <StartRunCard ctx={ctx} going={(active ? 1 : 0) + queue.length} onStart={(code, mode) => setStartFor({ code, mode })}
        onAll={() => setRunAll(true)} />
      {startFor && <RunStartModal ctx={ctx} code={startFor.code} mode={startFor.mode} onClose={() => setStartFor(null)} />}
      {runAll && <RunAllModal ctx={ctx} onClose={() => setRunAll(false)} />}

      <div className="grid gap-4 lg:grid-cols-2 items-start [&>*]:min-w-0">
        <Section title={active ? 'Running now' : 'Running now — nothing'}
          right={live ? <span className="text-[11px] text-grayscale-1100">refreshing every 8 s</span> : undefined}>
          {active ? <RunLine r={active} ctx={ctx} /> : (
            <div className="text-sm text-grayscale-1100">No worker is running. One run at a time, across all universities.</div>
          )}
          {queue.length > 0 && (
            <div className="flex flex-col">
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mt-1">Queue ({queue.length}) — they start one at a time, in this order</div>
              <div className="flex flex-col max-h-[22rem] overflow-auto">
                {queue.map(r => <RunLine key={str(r.id)} r={r} ctx={ctx} />)}
              </div>
            </div>
          )}
          {post.length > 0 && (
            <div className="flex flex-col">
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 mt-1">Search layers catching up ({post.length})</div>
              {post.map(r => <RunLine key={str(r.id)} r={r} ctx={ctx} />)}
            </div>
          )}
        </Section>
        <Section title={`Waiting for you (${needs})`}
          right={needs > 0 ? <Btn small onClick={() => ctx.goRuns({ status: 'awaiting_approval,held' })}>All in Runs →</Btn> : undefined}>
          {needs === 0 ? <div className="text-sm text-grayscale-1100">No run is awaiting approval or held.</div> : (
            <div className="flex flex-col">
              {awaiting.map(r => <RunLine key={str(r.id)} r={r} ctx={ctx} review />)}
              {held.map(r => <RunLine key={str(r.id)} r={r} ctx={ctx} review />)}
            </div>
          )}
        </Section>
      </div>

      {d.if_approved && <IfApprovedCard data={d.if_approved} ctx={ctx}
        autoApprove={typeof obj(d.settings).auto_approve === 'boolean' ? obj(d.settings).auto_approve : null} />}

      <div className="grid gap-4 lg:grid-cols-2 items-start [&>*]:min-w-0">
        <Section title={`Needs attention (${attention.length})`}
          right={kinds.length > 1 ? (
            <div className="flex gap-1 flex-wrap">
              {kinds.map(kk => (
                <button key={kk} type="button" onClick={() => setAttnKind(x => (x === kk ? '' : kk))}
                  className={`text-[11px] rounded-full border px-2 py-px ${attnKind === kk ? 'bg-primary-500 border-primary-500 text-white' : 'border-grayscale-600 text-grayscale-1200'}`}>
                  {ATTN[kk]?.label || kk} {attention.filter(a => str(a.kind) === kk).length}
                </button>
              ))}
            </div>
          ) : undefined}>
          {!attnShown.length ? <div className="text-sm text-grayscale-1100">Nothing needs attention.</div> : (
            <div className="flex flex-col max-h-[22rem] overflow-auto">
              {attnShown.map((a, i) => (
                <button key={i} type="button"
                  onClick={() => (num(a.run_id) !== null ? ctx.openRun(str(a.run_id)) : ctx.openUniversity(str(a.code)))}
                  className="flex items-center gap-2 text-left py-1.5 px-1 rounded hover:bg-grayscale-200 border-t border-grayscale-200 first:border-t-0">
                  <Pill tone={ATTN[str(a.kind)]?.tone || 'grey'}>{ATTN[str(a.kind)]?.label || str(a.kind)}</Pill>
                  <b className="text-sm text-grayscale-1300">{str(a.code)}</b>
                  <span className="text-xs text-grayscale-1100 truncate max-w-[10rem]">{str(a.name)}</span>
                  <span className="text-xs text-grayscale-1200 flex-1 min-w-0 truncate" title={str(a.text)}>{str(a.text)}</span>
                </button>
              ))}
            </div>
          )}
        </Section>
        <Section title={`Next scheduled runs (${scheduled.length} scheduled)`}
          right={<Btn small onClick={() => setShowAllDue(v => !v)}>{showAllDue ? 'Scheduled only' : `All ${dues.length}`}</Btn>}>
          {!dueList.length ? (
            <div className="text-sm text-grayscale-1100">
              No university has its schedule switched on. Turn one on in its drawer (Universities → a row → Schedule).
            </div>
          ) : (
            <div className="flex flex-col max-h-[22rem] overflow-auto">
              {dueList.map(x => (
                <div key={str(x.code)} className="flex items-center gap-2 py-1.5 border-t border-grayscale-200 first:border-t-0 text-sm">
                  <UniLink ctx={ctx} code={x.code} />
                  <span className="text-xs text-grayscale-1100 truncate max-w-[10rem]">{str(x.name)}</span>
                  {x.due ? <Pill tone="green">due now</Pill> : null}
                  {num(x.open_run_id) !== null && <RunLink ctx={ctx} id={x.open_run_id} />}
                  <span className="flex-1 min-w-0 text-xs text-grayscale-1200 truncate text-right" title={str(x.reason)}>
                    {x.next_at ? <><b>{fmtIST(x.next_at)}</b> <span className="text-grayscale-1100">({fmtAgo(x.next_at)})</span></> : '—'}
                    <span className="block text-[11px] text-grayscale-1100 truncate">{str(x.reason)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="All universities">
        <HealthGrid ctx={ctx} onRun={code => setStartFor({ code })} />
      </Section>

      <Section title="Recent runs" right={<Btn small onClick={() => ctx.goRuns({})}>Run history →</Btn>}>
        <RunTable runs={recent} ctx={ctx} maxHeight="22rem" tableId="recrawl_overview_recent"
          empty="No runs yet — start one with Start a run above." />
      </Section>
    </>
  );
};

export default Overview;
